// The ship: a three-masted, ~38 m (between perpendiculars) sixth-rate style
// privateer. One source of truth for hull lines, deck layout, spars, sails and
// guns, shared by the physics (buoyancy, sail aerodynamics) and the renderer.
//
// Ship-local frame: +X forward, +Y up, +Z starboard, origin on the centreline
// at midships on the design waterline. Units: metres.

import { smoothstep } from '../core/math';

// ---------------------------------------------------------------- hull lines

export const MAX_HALF_BEAM = 5.3;
export const KEEL_HALF = 0.2;
/** Station of maximum beam. */
const XM = 1.5;
/** Height fraction (keel to sheer) of maximum breadth. */
const T_MAX = 0.64;

/** Stem (bow profile) x at height fraction t. */
export function stemX(t: number): number {
  return 16.4 + 3.2 * Math.pow(t, 1.25);
}

/** Stern profile x at height fraction t (the counter overhangs aft). */
export function sternX(t: number): number {
  return -16.6 - 1.9 * Math.pow(t, 1.1);
}

/** Underside of the hull planking at the centreline. */
export function keelY(x: number): number {
  let y = -4.2;
  if (x > 9) y += 3.4 * Math.pow((x - 9) / 10.6, 2.1);
  if (x < -13) y += 0.9 * Math.pow((-13 - x) / 5.5, 2);
  return y;
}

/** Top of the hull side (rail). Rises into the quarterdeck and forecastle. */
export function sheerY(x: number): number {
  return 3.35 + 0.0021 * x * x + 2.0 * smoothstep(-3.5, -6.8, x) + 0.8 * smoothstep(9, 12.2, x);
}

export function yFromT(x: number, t: number): number {
  const k = keelY(x);
  return k + t * (sheerY(x) - k);
}

export function tFromY(x: number, y: number): number {
  const k = keelY(x);
  return (y - k) / (sheerY(x) - k);
}

/** Transom half-width fraction at height fraction t (0 below the counter). */
export function transomFraction(t: number): number {
  return 0.6 * smoothstep(0.38, 0.95, t);
}

/**
 * Hull half-breadth (outside of planking) at station x and height fraction t,
 * or -1 when (x, t) lies beyond the stem / stern profile.
 */
export function halfBreadth(x: number, t: number): number {
  const xs = sternX(t);
  const xb = stemX(t);
  if (x > xb + 1e-9 || x < xs - 1e-9) return -1;
  let f: number;
  if (x >= XM) {
    const u = Math.min(1, (x - XM) / (xb - XM));
    f = Math.pow(Math.max(0, 1 - Math.pow(u, 1.8 + 1.5 * t)), 0.5 + 0.2 * t);
  } else {
    const u = Math.min(1, (XM - x) / (XM - xs));
    const tr = transomFraction(t);
    f = tr + (1 - tr) * Math.pow(Math.max(0, 1 - Math.pow(u, 2.2 + 2.6 * t)), 0.85 - 0.3 * t);
  }
  const ends = Math.min(1, Math.abs(x - XM) / 17);
  const e = 2.7 - 1.3 * Math.pow(ends, 1.5);
  const sec = t < T_MAX ? 1 - Math.pow(1 - t / T_MAX, e) : 1 - 0.12 * Math.pow((t - T_MAX) / (1 - T_MAX), 1.6);
  return KEEL_HALF + (MAX_HALF_BEAM - KEEL_HALF) * sec * f;
}

/** Half-breadth at local (x, y), or -1 outside the hull's profile. */
export function halfBreadthXY(x: number, y: number): number {
  const t = tFromY(x, y);
  if (t < 0 || t > 1) return -1;
  return halfBreadth(x, t);
}

// ---------------------------------------------------------------- decks

export const DECK = {
  mainY: 2.05,
  quarterY: 4.3,
  foreY: 3.35,
  /** Break of the quarterdeck (its forward bulkhead). */
  quarterFrontX: -6.6,
  /** Break of the forecastle (its after bulkhead). */
  foreBackX: 12.4,
  plankThickness: 0.1,
  /** Hull side + bulwark thickness. */
  sideThickness: 0.28,
} as const;

/** Upper surface of whichever deck is exposed at station x. */
export function deckY(x: number): number {
  if (x < DECK.quarterFrontX) return DECK.quarterY + 0.0012 * (x + 12) * (x + 12) * 0.2;
  if (x > DECK.foreBackX) return DECK.foreY + 0.004 * (x - 12.4) * (x - 12.4);
  return DECK.mainY + 0.0017 * x * x;
}

/** Inner half-width of the deck at x (inside the bulwark). */
export function deckHalfWidth(x: number): number {
  const hb = halfBreadthXY(x, deckY(x));
  return hb < 0 ? -1 : Math.max(0, hb - DECK.sideThickness);
}

/** Extent of the hull at deck level. */
export const DECK_X_MIN = -18.1;
export const DECK_X_MAX = 18.9;

// ---------------------------------------------------------------- spars

export type MastId = 'fore' | 'main' | 'mizzen';

export interface MastSpec {
  id: MastId;
  x: number;
  /** Aft rake of the mast, radians. */
  rake: number;
  deckY: number;
  lowerTop: number;
  topmastTop: number;
  topgallantTop: number | null;
  radius: number;
}

export const MASTS: readonly MastSpec[] = [
  { id: 'fore', x: 13.0, rake: 0.02, deckY: DECK.foreY, lowerTop: 18.6, topmastTop: 29.2, topgallantTop: 37.4, radius: 0.36 },
  { id: 'main', x: 1.0, rake: 0.035, deckY: DECK.mainY, lowerTop: 20.6, topmastTop: 32.2, topgallantTop: 41.4, radius: 0.42 },
  { id: 'mizzen', x: -10.2, rake: 0.055, deckY: DECK.quarterY, lowerTop: 17.6, topmastTop: 26.8, topgallantTop: null, radius: 0.3 },
];

export function mast(id: MastId): MastSpec {
  return MASTS.find((m) => m.id === id)!;
}

/** Mast centreline x at height y (masts rake aft). */
export function mastXAt(m: MastSpec, y: number): number {
  return m.x - (y - m.deckY) * Math.tan(m.rake);
}

export interface YardSpec {
  id: string;
  mast: MastId;
  y: number;
  half: number;
  radius: number;
}

export const YARDS: readonly YardSpec[] = [
  { id: 'fore_course', mast: 'fore', y: 17.3, half: 8.4, radius: 0.2 },
  { id: 'fore_topsail', mast: 'fore', y: 26.6, half: 6.6, radius: 0.16 },
  { id: 'fore_topgallant', mast: 'fore', y: 34.8, half: 4.5, radius: 0.11 },
  { id: 'main_course', mast: 'main', y: 19.3, half: 9.6, radius: 0.23 },
  { id: 'main_topsail', mast: 'main', y: 29.4, half: 7.6, radius: 0.18 },
  { id: 'main_topgallant', mast: 'main', y: 38.6, half: 5.2, radius: 0.12 },
  { id: 'crossjack', mast: 'mizzen', y: 15.8, half: 6.8, radius: 0.15 },
  { id: 'mizzen_topsail', mast: 'mizzen', y: 24.6, half: 5.0, radius: 0.12 },
];

export function yard(id: string): YardSpec {
  return YARDS.find((y) => y.id === id)!;
}

/** Bowsprit: heel on the forecastle, steeved up at ~24 degrees. */
export const BOWSPRIT = {
  baseX: 16.6,
  baseY: 4.25,
  angle: 0.42,
  length: 12.5,
  radius: 0.3,
  jibboomStart: 9.5,
  jibboomLength: 9.5,
  jibboomRadius: 0.16,
};

/** Point on the bowsprit / jibboom line, s metres from its heel. */
export function bowspritPoint(s: number): { x: number; y: number } {
  return { x: BOWSPRIT.baseX + Math.cos(BOWSPRIT.angle) * s, y: BOWSPRIT.baseY + Math.sin(BOWSPRIT.angle) * s };
}

/** Spanker gaff and boom on the mizzen. */
export const SPANKER = {
  throatY: 14.6,
  peak: { x: -18.8, y: 17.4 },
  boomY: 6.5,
  clew: { x: -20.6, y: 6.3 },
  tackY: 6.9,
};

// ---------------------------------------------------------------- sails

export interface SquareSailSpec {
  kind: 'square';
  id: string;
  name: string;
  yard: string;
  /** Yard the clews are sheeted to, or null for courses (sheeted to deck). */
  footYard: string | null;
  footY: number;
  footHalf: number;
  cols: number;
  rows: number;
}

export interface ForeAftSailSpec {
  kind: 'foreaft';
  id: string;
  name: string;
  /** Luff runs head -> tack; sail pivots about this line. */
  head: { x: number; y: number };
  tack: { x: number; y: number };
  /** Clew with the sheet amidships (sheet angle 0). */
  clew: { x: number; y: number };
  /** Gaff sails have a peak (4th corner); headsails are triangles. */
  peak: { x: number; y: number } | null;
  cols: number;
  rows: number;
}

export type SailSpec = SquareSailSpec | ForeAftSailSpec;

const bsp = (s: number) => bowspritPoint(s);

export const SAILS: readonly SailSpec[] = [
  { kind: 'square', id: 'fore_course', name: 'Fore course', yard: 'fore_course', footYard: null, footY: 7.4, footHalf: 9.0, cols: 13, rows: 10 },
  { kind: 'square', id: 'fore_topsail', name: 'Fore topsail', yard: 'fore_topsail', footYard: 'fore_course', footY: 17.8, footHalf: 8.0, cols: 12, rows: 10 },
  { kind: 'square', id: 'fore_topgallant', name: 'Fore topgallant', yard: 'fore_topgallant', footYard: 'fore_topsail', footY: 27.1, footHalf: 6.1, cols: 10, rows: 8 },
  { kind: 'square', id: 'main_course', name: 'Main course', yard: 'main_course', footYard: null, footY: 7.6, footHalf: 10.3, cols: 14, rows: 10 },
  { kind: 'square', id: 'main_topsail', name: 'Main topsail', yard: 'main_topsail', footYard: 'main_course', footY: 19.8, footHalf: 9.2, cols: 13, rows: 10 },
  { kind: 'square', id: 'main_topgallant', name: 'Main topgallant', yard: 'main_topgallant', footYard: 'main_topsail', footY: 29.9, footHalf: 7.1, cols: 10, rows: 8 },
  { kind: 'square', id: 'mizzen_topsail', name: 'Mizzen topsail', yard: 'mizzen_topsail', footYard: 'crossjack', footY: 16.3, footHalf: 6.4, cols: 10, rows: 8 },
  {
    kind: 'foreaft', id: 'spanker', name: 'Spanker',
    head: { x: mastXAt(mast('mizzen'), SPANKER.throatY) - 0.3, y: SPANKER.throatY },
    tack: { x: mastXAt(mast('mizzen'), SPANKER.tackY) - 0.3, y: SPANKER.tackY },
    clew: SPANKER.clew, peak: SPANKER.peak, cols: 11, rows: 10,
  },
  { kind: 'foreaft', id: 'fore_staysail', name: 'Fore staysail', head: { x: 13.2, y: 17.2 }, tack: bsp(3.4), clew: { x: 13.9, y: 5.6 }, peak: null, cols: 8, rows: 10 },
  { kind: 'foreaft', id: 'jib', name: 'Jib', head: { x: 13.6, y: 26.2 }, tack: bsp(11.2), clew: { x: 17.6, y: 7.6 }, peak: null, cols: 9, rows: 12 },
  { kind: 'foreaft', id: 'flying_jib', name: 'Flying jib', head: { x: 13.8, y: 33.6 }, tack: bsp(16.8), clew: { x: 21.6, y: 10.4 }, peak: null, cols: 9, rows: 12 },
  // Gaff sails set by schooners in place of square canvas on fore and main.
  {
    kind: 'foreaft', id: 'fore_gaff', name: 'Fore gaff sail',
    head: { x: mastXAt(mast('fore'), 15.4) - 0.3, y: 15.4 }, tack: { x: mastXAt(mast('fore'), 5.0) - 0.3, y: 5.0 },
    clew: { x: 4.6, y: 4.6 }, peak: { x: 6.2, y: 18.2 }, cols: 11, rows: 10,
  },
  {
    kind: 'foreaft', id: 'main_gaff', name: 'Main gaff sail',
    head: { x: mastXAt(mast('main'), 18.0) - 0.3, y: 18.0 }, tack: { x: mastXAt(mast('main'), 3.4) - 0.3, y: 3.4 },
    clew: { x: -9.4, y: 5.9 }, peak: { x: -7.6, y: 21.4 }, cols: 12, rows: 11,
  },
];

/** Which mast carries a sail and at what level (0 lower, 1 topmast, 2 topgallant). */
export function sailMount(id: string): { mast: MastId; level: number } {
  switch (id) {
    case 'fore_course': case 'fore_gaff': return { mast: 'fore', level: 0 };
    case 'fore_topsail': return { mast: 'fore', level: 1 };
    case 'fore_topgallant': return { mast: 'fore', level: 2 };
    case 'main_course': case 'main_gaff': return { mast: 'main', level: 0 };
    case 'main_topsail': return { mast: 'main', level: 1 };
    case 'main_topgallant': return { mast: 'main', level: 2 };
    case 'mizzen_topsail': return { mast: 'mizzen', level: 1 };
    case 'spanker': return { mast: 'mizzen', level: 0 };
    // Headsails hang from the fore stays.
    case 'fore_staysail': return { mast: 'fore', level: 0 };
    case 'jib': return { mast: 'fore', level: 1 };
    default: return { mast: 'fore', level: 2 };
  }
}

export interface SailPlan {
  name: string;
  deploy: Record<string, number>;
}

/** Sail plans the helm can step through (index 0 = everything furled). */
export const SAIL_PLANS: readonly SailPlan[] = [
  { name: 'Furled', deploy: {} },
  { name: 'Storm canvas', deploy: { fore_topsail: 0.4, main_topsail: 0.4, fore_staysail: 1 } },
  { name: 'Fighting sail', deploy: { fore_topsail: 1, main_topsail: 1, mizzen_topsail: 1, jib: 1, fore_staysail: 1, spanker: 1 } },
  { name: 'Plain sail', deploy: { fore_course: 1, main_course: 1, fore_topsail: 1, main_topsail: 1, mizzen_topsail: 1, jib: 1, fore_staysail: 1, spanker: 1 } },
  {
    name: 'All plain sail',
    deploy: { fore_course: 1, main_course: 1, fore_topsail: 1, main_topsail: 1, mizzen_topsail: 1, fore_topgallant: 1, main_topgallant: 1, jib: 1, flying_jib: 1, fore_staysail: 1, spanker: 1 },
  },
];

// ---------------------------------------------------------------- guns

export interface CannonSpec {
  /** +1 starboard, -1 port. */
  side: 1 | -1;
  x: number;
  deck: 'main' | 'quarter';
  /** Barrel length, m. */
  length: number;
  bore: number;
}

const MAIN_GUNS = [-5.1, -2.2, 0.0, 3.4, 6.2, 9.0];
const QUARTER_GUNS = [-9.0, -14.6];

export const CANNONS: readonly CannonSpec[] = [
  ...MAIN_GUNS.flatMap((x) => ([1, -1] as const).map((side) => ({ side, x, deck: 'main' as const, length: 2.6, bore: 0.12 }))),
  ...QUARTER_GUNS.flatMap((x) => ([1, -1] as const).map((side) => ({ side, x, deck: 'quarter' as const, length: 1.9, bore: 0.09 }))),
];

/** Gunport opening in the bulwark, in (x, y) hull coordinates. */
export function gunportRect(c: CannonSpec): { x0: number; x1: number; y0: number; y1: number } {
  const w = c.deck === 'main' ? 0.72 : 0.56;
  const y = deckY(c.x);
  return { x0: c.x - w / 2, x1: c.x + w / 2, y0: y + 0.28, y1: y + 0.28 + w * 0.85 };
}

/** Cannon trunnion height and inboard position when run out. */
export function cannonMount(c: CannonSpec): { x: number; y: number; z: number } {
  const r = gunportRect(c);
  const y = (r.y0 + r.y1) / 2 - 0.02;
  const hb = halfBreadthXY(c.x, y);
  return { x: c.x, y, z: c.side * (hb - DECK.sideThickness - c.length * 0.42) };
}

// ---------------------------------------------------------------- deck fittings

export interface DeckObstacle {
  kind: 'circle' | 'box';
  x: number;
  z: number;
  /** circle radius, or box half extents */
  r?: number;
  hx?: number;
  hz?: number;
}

export const HATCHES = [
  { x: 4.8, hx: 1.25, hz: 1.05, grating: true },
  { x: 9.6, hx: 0.9, hz: 0.8, grating: true },
  { x: -3.3, hx: 0.8, hz: 0.8, grating: false },
];

export const CAPSTAN = { x: -4.9, r: 0.62 };
export const WHEEL = { x: -12.9, binnacleX: -11.7 };
export const BELFRY = { x: 12.1 };
/** Ship's boat stowed on the skid beams over the main hatch. */
export const LONGBOAT = { x: 5.2, length: 6.2, beam: 1.9, y: DECK.mainY + 1.15 };

/** Shroud channels outboard at each mast: centre x, length. */
export const CHANNELS: Readonly<Record<MastId, readonly [number, number]>> = { fore: [13.3, 4.6], main: [1.0, 5.6], mizzen: [-10.0, 4.0] };

/** Companion ladders between the decks: the crew walk these, so the model must have stairs here. */
export const LADDERS = [
  // Main deck <-> quarterdeck, both sides.
  { low: { x: DECK.quarterFrontX + 1.8, z: 2.6 }, high: { x: DECK.quarterFrontX - 0.4, z: 2.6 }, up: 'quarter' },
  { low: { x: DECK.quarterFrontX + 1.8, z: -2.6 }, high: { x: DECK.quarterFrontX - 0.4, z: -2.6 }, up: 'quarter' },
  // Main deck <-> forecastle.
  { low: { x: DECK.foreBackX - 1.3, z: 1.8 }, high: { x: DECK.foreBackX + 0.4, z: 1.8 }, up: 'fore' },
  { low: { x: DECK.foreBackX - 1.3, z: -1.8 }, high: { x: DECK.foreBackX + 0.4, z: -1.8 }, up: 'fore' },
] as const;

export const MAIN_DECK_OBSTACLES: readonly DeckObstacle[] = [
  ...MASTS.filter((m) => m.id === 'main').map((m) => ({ kind: 'circle' as const, x: m.x, z: 0, r: 0.9 })),
  ...HATCHES.map((h) => ({ kind: 'box' as const, x: h.x, z: 0, hx: h.hx + 0.15, hz: h.hz + 0.15 })),
  { kind: 'circle', x: CAPSTAN.x, z: 0, r: CAPSTAN.r + 0.1 },
];
